import api from './axios'
import { Dealer, CreateDealerData, UpdateDealerData } from '@crm-src/types/dealer'

/** Params for the PAGINATED dealer list. Sending `page` or `per_page` is what
 * opts in to the envelope — see `getPageByReseller`. */
export interface DealerListParams {
  search?: string
  page?: number
  per_page?: number
}

export interface DealerPage {
  dealers: Dealer[]
  pagination: { page: number; per_page: number; total_count: number; total_pages: number }
}

class DealersApiService {
  /**
   * The BARE, unpaginated array — every dealer of a reseller.
   *
   * Deliberately sends no paging params, and that is the contract: crm-api
   * answers this shape unless `page`/`per_page` is present, because the
   * reseller PORTAL's copy of this client reads `response.data` as an array
   * and its deploys are not atomic with crm-api's. Use `getPageByReseller`
   * for a list that pages.
   */
  async getByReseller(resellerId: string): Promise<Dealer[]> {
    const response = await api.get(`/resellers/${resellerId}/dealers`)
    return response.data
  }

  /** One page of a reseller's dealers, searched server-side. */
  async getPageByReseller(resellerId: string, params: DealerListParams = {}): Promise<DealerPage> {
    const response = await api.get(`/resellers/${resellerId}/dealers`, {
      // `per_page` is what opts in to the envelope; default it so a caller
      // that only passes a search term still gets a pagination block back
      // instead of a bare array it would then have to sniff for.
      params: { per_page: 10, ...params },
    })
    return response.data
  }

  async create(resellerId: string, data: CreateDealerData): Promise<Dealer> {
    const response = await api.post(`/resellers/${resellerId}/dealers`, { dealer: data })
    return response.data
  }

  async update(id: string, data: UpdateDealerData): Promise<Dealer> {
    const response = await api.patch(`/dealers/${id}`, { dealer: data })
    return response.data
  }

  async delete(id: string): Promise<void> {
    await api.delete(`/dealers/${id}`)
  }
}

export const dealersApiService = new DealersApiService()
export default dealersApiService



